import React from 'react';
import ReactDOM from 'react-dom';
import { Component, Q } from '@fullstory/tomato';
import { OverlaysView } from '@fullstory/tomato-views/views';
import { Masker } from './masker';
import { AbstractRendererImpl } from '../abstract_renderer';
import { CloneRect, ElemBounds } from './rectangles';
import { RankIndicator } from './rankindicator';
import { CanvasRendererOverlayDelegate } from '@fullstory/playback-mobile';
import { Size } from '@fullstory/shared/viewport';
import {
  ClickAreaOverlayContainer,
  ClickAreaOverlayDisplayOptions,
  ClickDetails,
  ClickMapDisplayOptions,
  ConversionOverlayContainer,
  ConversionOverlayDisplayOptions,
  ElementConversionStats,
  OverlayColors,
  OverlayHighlightDetails,
  ScrollMapDisplayOptions,
  ScrollMapsFacet,
  ScrollMapsOverlay,
  elemIdIdent,
} from '@fullstory/page-insights-ui';
import { ControlledIFrameElement } from '@fullstory/controlled-iframe';
import { findFirstInternalScrollNode } from '../scrollmapUtils';
import { ActivityMapsHost, ClickFacet, MatchingElement } from '../page-insights/activitymaps';
import { ColorMap } from '../page-insights/color_map';
import { IntlProvider } from '@fullstory/intl';
import { ThemeProvider } from '@fullstory/theme';
import { debounce, memoize } from 'lodash';
import { isNonNull } from '@fullstory/typeutils';
import { SimpleRect } from '@fullstory/util/rect';

export const INDICATOR_SIZE = 28; // This needs to match $indicatorWidth in shared.scss
const FORCE_FACTOR = 0.1; // Determines how far the indicators move on each iteration of the force algorithm.
const INDICATOR_PAGE_SIZE = 5; // How many indicators constitute a "page".
const INITIAL_RESIZE_DELAY = 10;

// Some coherent modality of overlays controlled by the RendererImpl.
export interface OverlayMode {
  start();

  stop();
}

export class Point {
  x: number;
  y: number;
}

// Class that controls laying out RankIndicators so they don't all naively fall on top of each other.
export class LayoutController {
  private _layoutViewport!: SimpleRect;

  constructor() {
    // Use the layout viewport of the current document as a reasonable initial
    // value.
    this.setLayoutViewportSize({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }

  public setLayoutViewportSize({ width, height }: Readonly<Size>): void {
    this._layoutViewport = {
      left: 0,
      top: 0,
      right: width,
      bottom: height,
      width,
      height,
    };
  }

  public get layoutViewport(): SimpleRect {
    return this._layoutViewport;
  }

  // Calculates positions for the highlight indicators and renders them.
  public layoutElems(
    elems: ElemBounds[],
    indicators: Map<ElemBounds, RankIndicator>,
    inverseScale: number,
    animate: boolean,
    allowHighlightRelatedElements: boolean,
  ) {
    let centroids: Point[] = [];
    let scaledSize: number = -1;

    elems.forEach(elem => {
      const indicator = indicators.get(elem);
      if (!indicator) {
        return;
      }

      if (scaledSize == -1) {
        // This assumes all indicators are the same size, which they are (currently).
        scaledSize = indicator.getScaledSize(inverseScale);
      }
      centroids.push(indicator.getInitialCentroid(elem.r(), inverseScale));
    });

    if (centroids.length == 0) {
      // There were no indicators; return.
      return;
    }

    // Use a force-directed graph algorithm to move intersecting points.
    // TODO(stephanie): Choose a more meaningful number of iterations.
    for (let i = 0; i < 10; i++) {
      centroids = this.moveCentroids(centroids, scaledSize);
    }

    // Track elems for resize events.
    elems.forEach((elem, i) => {
      const indicator = indicators.get(elem);
      if (!indicator) {
        return;
      }

      // New bounding box.
      const left = centroids[i].x;
      const top = centroids[i].y;
      const newRect = {
        left,
        top,
        right: left + INDICATOR_SIZE,
        bottom: top + INDICATOR_SIZE,
        width: INDICATOR_SIZE,
        height: INDICATOR_SIZE,
      } as SimpleRect;

      // Position and render bounding boxes and indicators.
      let relatedRecs: SimpleRect[];
      if (allowHighlightRelatedElements) {
        relatedRecs = elem.allRects();
      } else {
        relatedRecs = [elem.r()];
      }

      indicator.position(relatedRecs, newRect, animate);
    });
  }

  // Points contains the current centroids for the markers, size is the width/height of each marker.
  // This method users a basic force algorithm to calculate the repulsion between indicators and move them accordingly.
  // If 2 indicators do not overlap, their repulsion is 0.
  private moveCentroids(points: Point[], size: number): Point[] {
    const output: Point[] = [];

    // For each point, sum the forces acting on it and then move it a little bit.
    for (let i = 0; i < points.length; i++) {
      let xForce = 0;
      let yForce = 0;
      for (let j = 0; j < points.length; j++) {
        if (i == j) {
          continue;
        }

        const xDist = points[i].x - points[j].x;
        const yDist = points[i].y - points[j].y;
        // Only add a force if the 2 overlap.
        if (Math.abs(xDist) < size && Math.abs(yDist) < size) {
          xForce += xDist;
          yForce += yDist;
        }
      }
      output.push({
        x: points[i].x + FORCE_FACTOR * xForce,
        y: points[i].y + FORCE_FACTOR * yForce,
      } as Point);
    }
    return output;
  }

  /**
   * @param indicator The indicator to position
   * @param scale The amount that the renderer has been scaled. The `lastIndicatorRect` will be
   * as if the renderer hasn't scaled so we need to apply it to the bounding boxes here
   * in order to execute the clamping logic correctly.
   */
  clampToViewport(indicator: RankIndicator, scale: number) {
    // Ensure that the selected element is within the viewport bounds.
    const view = indicator.view();
    const r = indicator.lastIndicatorRect();
    this.clearOffscreenPositions(view);
    //
    if (r.bottom * scale + INDICATOR_SIZE / 2 < this._layoutViewport.top) {
      view.addClass('offscreen').addClass('above');
      const clamped = CloneRect(r);
      clamped.top = this._layoutViewport.top + 3 * INDICATOR_SIZE;
      clamped.bottom = clamped.top + clamped.height;
      indicator.positionIndicator(clamped);
    } else if (r.top * scale > this._layoutViewport.bottom) {
      view.addClass('offscreen').addClass('below');
      const clamped = CloneRect(r);
      clamped.top = this._layoutViewport.bottom - (r.height + 3 * INDICATOR_SIZE);
      clamped.bottom = clamped.top + r.height;
      indicator.positionIndicator(clamped);
    }
  }

  clearOffscreenPositions(view: Q.OfOne) {
    view
      .removeClass('offscreen')
      .removeClass('above')
      .removeClass('below')
      .removeClass('left')
      .removeClass('right');
  }
}

type overlayMode = 'scrollmap' | 'clickmap' | 'area-clickmap' | 'conversion' | null;

// Overlays we render on top of playback or inspection DOM. Deals with scaling and translating between
// the outer screen coordinates and the playback frame scaled coordinates.
export abstract class Overlays
  extends Component<OverlaysView>
  implements CanvasRendererOverlayDelegate
{
  #pointerAndTouchIndicatorsVisible = true;
  #gotPointerEvent = false;
  #gotTouchEvent = false;
  private _curTouchElems: { [id: string]: Q.OfOne };
  private _masker: Masker;
  private _resizeOps: Array<
    (visualInverseScale: number, frameInverseScale: number, zoomInverseScale: number) => void
  > = [];
  private _layoutController: LayoutController;
  private _curHighlightedElems: MatchingElement[] = [];
  private _rankIndicators: Map<ElemBounds, RankIndicator> = new Map();
  private _curSelectedIdx = -1;
  private _scrollFacet: ScrollMapsFacet;
  private _frame: ControlledIFrameElement;
  private _playbackContainer: Q.OfOne;
  private _scrollMapDisplayOptions: ScrollMapDisplayOptions = {
    displayMedianPageFold: false,
    showStaticMarkers: false,
  };
  private _hoveringRankIndicator: RankIndicator | undefined;
  private _overlayMode: overlayMode;
  private _activityMapsHost: ActivityMapsHost;
  private _clickDetails: ClickDetails;
  private _areaClickMapDisplayOptions: ClickAreaOverlayDisplayOptions;
  private _conversionAreaDisplayOptions: ConversionOverlayDisplayOptions;

  protected constructor(private _renderer: AbstractRendererImpl) {
    super(_renderer.view().overlays);
    this._layoutController = new LayoutController();
    this.removeAllTouches();
  }

  public hideGlassPane() {
    // Allow inspection to happen through the glass pane
    this.view().setCss('pointer-events', 'none');
  }

  public showGlassPane() {
    // Restore the glass pane
    this.view().setCss('pointer-events', 'auto');
  }

  public resize() {
    this.inverseScale(this.view().cursor);
    this._resizeOps.forEach(rf => {
      rf(
        this.currentVisualInverseScale(),
        this.currentFrameInverseScale(),
        this.currentZoomInverseScale(),
      );
    });
  }

  private currentVisualInverseScale(): number {
    const visualZoom = this._renderer.currentVisualScale();
    return Overlays.calculateInverseScale(visualZoom);
  }

  private currentFrameInverseScale(): number {
    const frameScale = this._renderer.currentFrameScale();
    return Overlays.calculateInverseScale(frameScale);
  }

  private currentZoomInverseScale(): number {
    const zoom = this._renderer.currentZoomScale();
    return Overlays.calculateInverseScale(zoom);
  }

  static calculateInverseScale(scale: number): number {
    // To avoid nasty math errors, first make sure the scale is valid before doing computations.
    return Overlays.isInvalidScale(scale) ? 1 : 1 / scale;
  }

  static isInvalidScale(scale: number): boolean {
    return !scale || !isFinite(scale) || scale === 0 || Object.is(scale, -0);
  }

  public clearScrollMaps() {
    if (this._masker) {
      this._masker.hide();
    }
    ReactDOM.unmountComponentAtNode(this.view().scrollmaps.elem());
    this._resizeOps = [];
  }

  public placeScrollMaps(
    scrolls: ScrollMapsFacet,
    displayOptions: ScrollMapDisplayOptions,
    frame: ControlledIFrameElement,
    playbackContainer: Q.OfOne,
  ) {
    if (this._masker) {
      this._masker.hide();
    }
    this._scrollFacet = scrolls;
    this._frame = frame;
    this._playbackContainer = playbackContainer;
    this._scrollMapDisplayOptions = displayOptions;
    this._overlayMode = 'scrollmap';

    // Remember all the things that might need to be resized.
    this._resizeOps.push(
      (visualInverseScale: number, frameInverseScale: number, zoomInverseScale: number) => {
        this.layoutScrollMaps(visualInverseScale, frameInverseScale, zoomInverseScale);
      },
    );

    this.layoutScrollMaps();

    // Schedule an async resize to scale things appropriately.
    setTimeout(() => {
      this.resize();
    }, INITIAL_RESIZE_DELAY);
  }

  public placeAreaHighlights({
    host,
    elems,
    displayOptions,
    playbackContainer,
    clickFacet,
  }: {
    host: ActivityMapsHost;
    elems: MatchingElement[];
    displayOptions: ClickAreaOverlayDisplayOptions;
    playbackContainer: Q.OfOne;
    clickFacet: ClickFacet;
  }) {
    this.performCommonAreaHighlightsSetup({
      overlayMode: 'area-clickmap',
      host,
      elems,
      playbackContainer,
      resizeOperation: (
        visualInverseScale: number,
        frameInverseScale: number,
        zoomInverseScale: number,
      ) => {
        this.layoutAreaClickMaps(
          displayOptions,
          visualInverseScale,
          frameInverseScale,
          zoomInverseScale,
        );
      },
    });

    this._clickDetails = clickFacet;
    this._areaClickMapDisplayOptions = displayOptions;

    this.layoutAreaClickMaps(displayOptions);

    // Schedule an async resize to scale things appropriately.
    setTimeout(() => {
      this.resize();
    }, INITIAL_RESIZE_DELAY);
  }

  public placeConversionHighlights({
    host,
    elems,
    displayOptions,
    playbackContainer,
  }: {
    host: ActivityMapsHost;
    elems: MatchingElement[];
    displayOptions: ConversionOverlayDisplayOptions;
    playbackContainer: Q.OfOne;
  }) {
    this.performCommonAreaHighlightsSetup({
      overlayMode: 'conversion',
      host,
      elems,
      playbackContainer,
      resizeOperation: (
        visualInverseScale: number,
        frameInverseScale: number,
        zoomInverseScale: number,
      ) => {
        this.layoutConversionMaps(
          displayOptions,
          visualInverseScale,
          frameInverseScale,
          zoomInverseScale,
        );
      },
    });

    this._conversionAreaDisplayOptions = displayOptions;
    this.layoutConversionMaps(displayOptions);

    // Schedule an async resize to scale things appropriately.
    setTimeout(() => {
      this.resize();
    }, INITIAL_RESIZE_DELAY);
  }

  public layoutAreaClickMaps(
    displayOptions: ClickAreaOverlayDisplayOptions,
    visualInverseScale?: number,
    frameInverseScale?: number,
    zoomInverseScale?: number,
  ) {
    const highlightsOverlayElement: HTMLElement = this.view().highlights.elem();

    // Get positions of elements
    const clickOverlayDetails: OverlayHighlightDetails[] = this.getClickOverlayDetails(
      this._curHighlightedElems,
    );

    // Update the client rect since that's updated on scroll and won't match the cache
    for (let i = 0; i < clickOverlayDetails.length; i++) {
      clickOverlayDetails[i].clientRect = this._curHighlightedElems[i].r();
    }

    ReactDOM.render(
      <IntlProvider>
        <ThemeProvider>
          <ClickAreaOverlayContainer
            playbackScaleFactor={this.getPlaybackScaleFactor({
              zoomInverseScale,
              frameInverseScale,
              visualInverseScale,
            })}
            inverseScale={this.currentVisualInverseScale()}
            clickOverlayDetails={clickOverlayDetails}
            selectedItem={this._curSelectedIdx + 1}
            onSelect={rank => this._activityMapsHost.selectAt(rank - 1)}
            clickDetails={this._clickDetails}
            displayOptions={displayOptions}
            layoutViewport={this._layoutController.layoutViewport}
          />
        </ThemeProvider>
      </IntlProvider>,
      highlightsOverlayElement,
    );
  }

  public layoutConversionMaps(
    displayOptions: ConversionOverlayDisplayOptions,
    visualInverseScale?: number,
    frameInverseScale?: number,
    zoomInverseScale?: number,
  ) {
    const highlightsOverlayElement: HTMLElement = this.view().highlights.elem();

    // Get positions of elements
    const clickOverlayDetails: OverlayHighlightDetails[] = this.getConversionOverlayDetails(
      this._curHighlightedElems,
      displayOptions.elementConversionStats,
    );

    // Update the client rect since that's updated on scroll and won't match the cache
    for (let i = 0; i < clickOverlayDetails.length; i++) {
      clickOverlayDetails[i].clientRect = this._curHighlightedElems[i].r();
    }

    ReactDOM.render(
      <IntlProvider>
        <ThemeProvider>
          <ConversionOverlayContainer
            playbackScaleFactor={this.getPlaybackScaleFactor({
              zoomInverseScale,
              frameInverseScale,
              visualInverseScale,
            })}
            inverseScale={this.currentVisualInverseScale()}
            clickOverlayDetails={clickOverlayDetails}
            selectedRank={this._curSelectedIdx + 1}
            onSelect={rank => this._activityMapsHost.selectAt(rank - 1)}
            displayOptions={displayOptions}
            layoutViewport={this._layoutController.layoutViewport}
            conversionEventLabel={displayOptions.conversionEventLabel}
          />
        </ThemeProvider>
      </IntlProvider>,
      highlightsOverlayElement,
    );
  }

  public layoutScrollMaps(
    visualInverseScale?: number,
    frameInverseScale?: number,
    zoomInverseScale?: number,
  ) {
    // Try to find the internal selector. It's possible the page has changed layouts to trying all the known selectors
    // and default to document scrolling if we can't find anything.
    const internalScrollElement: Element = findFirstInternalScrollNode(
      this._frame.contentDocument,
      this._scrollFacet.internalScrollSelectors,
    );

    let gradientBounds;
    let internalScrollContainerBounds;
    let internalScrollViewportBounds;

    const docHeight = Math.max(
      this._frame.contentDocument?.scrollingElement.scrollHeight,
      this._frame.contentDocument?.documentElement.getBoundingClientRect().height,
    );
    const docWidth = Math.max(
      this._frame.contentDocument?.scrollingElement.scrollWidth,
      this._frame.contentDocument?.documentElement.getBoundingClientRect().width,
    );

    const scrollMapsOverlayElement: HTMLElement = this.view().scrollmaps.elem();

    if (internalScrollElement) {
      const boundingClientRect = internalScrollElement.getBoundingClientRect();

      // The scroll map overlay takes up the entire player. For internal scrolls, it's possible we have a header or
      // footer div. The overlay needs to be the entire page for click maps, etc but for the scroll maps area div,
      // we need to set the top and height explicitly.
      scrollMapsOverlayElement.style.height = `${boundingClientRect.height}px`;
      scrollMapsOverlayElement.style.width = `${boundingClientRect.width}px`;
      scrollMapsOverlayElement.style.top = `${boundingClientRect.top}px`;
      scrollMapsOverlayElement.style.bottom = `${boundingClientRect.bottom}px`;
      scrollMapsOverlayElement.style.left = `${boundingClientRect.left}px`;

      internalScrollViewportBounds = {
        top: boundingClientRect.top,
        bottom: boundingClientRect.bottom,
        left: boundingClientRect.left,
        right: boundingClientRect.right,
        width: boundingClientRect.width,
        height: boundingClientRect.height,
      };

      const scrollElementBounds = {
        ...internalScrollViewportBounds,
        // When calling the document.getBoundingClientRect(), the top value represents the scroll value.
        // When doing this for elements, it stays static at viewport height. Coalesce the top value to make it
        // work like the document value for downstream objects. scrollTop is always positive while top tends to be
        // negative.
        top: -internalScrollElement.scrollTop, // scrollTop is always positive while we want a negative number for top
        bottom: boundingClientRect.bottom,
        width: Math.max(internalScrollElement.scrollWidth, boundingClientRect.width),
        height: Math.max(internalScrollElement.scrollHeight, boundingClientRect.height),
      };
      internalScrollContainerBounds = { ...scrollElementBounds };

      // Setup the gradient bounds to match the internal element dimensions
      gradientBounds = {
        // Sticky to 0 when we haven't scrolled yet
        top: Math.min(boundingClientRect.top - internalScrollElement.scrollTop, 0),
        left: boundingClientRect.left,
        right: boundingClientRect.right,
        bottom: boundingClientRect.bottom,
        width: scrollElementBounds.width,
        height: scrollElementBounds.height - boundingClientRect.top,
        zIndex: 0,
      };
    }

    const pageBoundingClientRect =
      this._frame.contentDocument.documentElement.getBoundingClientRect();
    const pageBounds = internalScrollElement
      ? internalScrollViewportBounds
      : {
          top: pageBoundingClientRect.top,
          left: pageBoundingClientRect.left,
          right: pageBoundingClientRect.right,
          bottom: pageBoundingClientRect.bottom,
          width: pageBoundingClientRect.width,
          height: pageBoundingClientRect.height,
        };

    const playbackContainerBounds = this._playbackContainer.e.getBoundingClientRect();
    let playbackContainerHeight = playbackContainerBounds.height;
    let playbackContainerWidth = playbackContainerBounds.width;

    if (this._playbackContainer.css('height') !== '100%') {
      playbackContainerHeight = parseInt(this._playbackContainer.css('height'), 10);
    }
    if (this._playbackContainer.css('width') !== '100%') {
      playbackContainerWidth = parseInt(this._playbackContainer.css('width'), 10);
    }

    const overlayContainerBounds = this.view().scrollmaps.e.getBoundingClientRect();

    ReactDOM.render(
      <IntlProvider>
        <ThemeProvider>
          <ScrollMapsOverlay
            facetData={this._scrollFacet}
            pageBounds={{
              top: pageBounds.top,
              left: pageBounds.left,
              right: pageBounds.right,
              bottom: pageBounds.bottom,
              width: docWidth,
              height: docHeight,
              zIndex: 0,
            }}
            gradientBoundsOverride={gradientBounds}
            playbackContainerBounds={{
              top: playbackContainerBounds.top,
              left: playbackContainerBounds.left,
              right: playbackContainerBounds.right,
              bottom: playbackContainerBounds.bottom,
              width: playbackContainerWidth,
              height: playbackContainerHeight,
              zIndex: 0,
            }}
            overlayContainerBounds={{
              top: overlayContainerBounds.top,
              left: overlayContainerBounds.left,
              right: overlayContainerBounds.right,
              bottom: overlayContainerBounds.bottom,
              width: overlayContainerBounds.width,
              height: overlayContainerBounds.height,
              zIndex: 0,
            }}
            medianPageFold={this._scrollFacet.medianPageFold}
            scrollMapDisplayOptions={this._scrollMapDisplayOptions}
            visualInverseScale={
              visualInverseScale ? visualInverseScale : this.currentVisualInverseScale()
            }
            frameInverseScale={
              frameInverseScale ? frameInverseScale : this.currentFrameInverseScale()
            }
            zoomInverseScale={zoomInverseScale ? zoomInverseScale : this.currentZoomInverseScale()}
            internalScroll={!!internalScrollElement}
            internalScrollContainerBounds={internalScrollContainerBounds}
            internalScrollViewportBounds={internalScrollViewportBounds}
          />
        </ThemeProvider>
      </IntlProvider>,
      scrollMapsOverlayElement,
    );
  }

  public togglePageFoldDataDisplay(checked: boolean) {
    this._scrollMapDisplayOptions.displayMedianPageFold = checked;
  }

  public clearHighlights() {
    if (this._masker) {
      this._masker.hide();
    }
    this._overlayMode = null;
    this.view().highlights.setHtml('');
    this._resizeOps = [];
    for (const [_, indicator] of this._rankIndicators) {
      indicator.dispose();
    }
    this._curSelectedIdx = -1;
    this._curHighlightedElems = [];
    this._rankIndicators = new Map();
  }

  public clearAreaOverlays() {
    ReactDOM.unmountComponentAtNode(this.view().highlights.elem());
    this.view().highlights.setHtml('');
    this._resizeOps = [];
  }

  public highlight(
    host: ActivityMapsHost,
    elems: MatchingElement[],
    displayOptions: ClickMapDisplayOptions,
    exportMode: boolean,
  ) {
    this._curHighlightedElems = elems;
    if (!this._masker) {
      this._masker = new Masker(this._renderer.view().overlays.masker);
    }
    this._overlayMode = 'clickmap';

    const colors = new ColorMap(elems.map(x => x.stats().NumClicks));
    colors.clearAllGradientText();

    const customIndicatorClasses = exportMode ? ['export-mode'] : null;
    const rankIndicators: RankIndicator[] = [];

    const addRankOperations = elems.map((elem, i) => () => {
      const indicator = new RankIndicator(
        this.view().highlights,
        elem.stats(),
        displayOptions,
        customIndicatorClasses,
      );
      rankIndicators.push(indicator);
      const color = colors.getGradientIndex(elem.stats().NumClicks);
      indicator.setRank(i, color);
      indicator.onClick = () => host.selectAt(i); // Call back out to the side bar to select the appropriate element.
      this._rankIndicators.set(elem, indicator);

      // Remember all the things that might need to be resized.
      this._resizeOps.push(
        (visualInverseScale: number, frameInverseScale: number, zoomInverseScale: number) => {
          indicator.onResize(zoomInverseScale * (frameInverseScale / visualInverseScale));
        },
      );

      if (i == elems.length - 1) {
        // Kill all animations. Run it after the final element is finished with its transition.
        indicator.view().on('transitionend', () => {
          setTimeout(() => {
            this._rankIndicators.forEach(x => x.disableTransition()); // Disable transitions so scroll isn't janky.
          }, 100); // Allow time for layout and paint prior to modifying classes.
        });
      }
    });

    // Add ranks in reverse so lower numbers ones are shown above higher, less important ranks
    addRankOperations.reverse().forEach(operation => operation());

    const getEnableBoxPointerEventsFunction = memoize((rankIndicator: RankIndicator) =>
      debounce(() => rankIndicator.enabledBoxPointerEvents(), 100, {
        trailing: true,
      }),
    );

    // Add hovers actions. It's possible for items to be within other items. These actions will find the smallest
    // area and use that one for the hover action.
    if (displayOptions.enableMouseOverHighlights) {
      for (const rankIndicator of rankIndicators) {
        rankIndicator.setBoxMouseAction({
          onMouseMove: e => this.setRelatedHoverRankIndicator(e, rankIndicators),
          onMouseOut: e => {
            if (!this._hoveringRankIndicator?.getRelatedElementArea(e)) {
              this._hoveringRankIndicator?.setHover(false);
              this._hoveringRankIndicator = null;
            }
          },
          onClick: () => {
            host.selectAt(this._hoveringRankIndicator?.rank - 1);
          },
          onWheel: e => {
            // Having "pointer-events: all" makes it to the playback content doesn't process the wheel event
            // and thus it can't be scrolled. Trying to fire off a wheel event from the related element in playback
            // doesn't actually move the element. The solution to this is to temporally disable pointer events handling
            // so that it can be processed natively.
            const relatedIndicators = rankIndicators.filter(
              rankIndicator => rankIndicator.getRelatedElementArea(e) > 0,
            );

            // Disable mouse events for all RankIndicators under the current mouse position and enable shortly afterwards
            for (const relatedIndicator of relatedIndicators) {
              relatedIndicator.disableBoxPointerEvents();
              getEnableBoxPointerEventsFunction(relatedIndicator)();
            }
            e.stopPropagation();
          },
        });
      }
    }

    // Position things.
    this.layout(displayOptions.allowHighlightRelatedElements, displayOptions.useTransitions);
    this._resizeOps.push(
      (visualInverseScale: number, frameInverseScale: number, zoomInverseScale: number) => {
        this.layout(displayOptions.allowHighlightRelatedElements, displayOptions.useTransitions);
      },
    );

    // Schedule an async resize to scale things appropriately.
    setTimeout(() => {
      this.resize();
    }, 10);
  }

  /**
   * It's possible for elements related to a RankIndicator to be within another. This function will find the smallest
   * RankIndicator box that's related to the given mouse event since it's assumed that's what the user is actually
   * interested in.
   */
  private setRelatedHoverRankIndicator(e: MouseEvent, rankIndicators: RankIndicator[]) {
    let smallestRankIndicator: RankIndicator;
    let smallestArea = Number.MAX_SAFE_INTEGER;

    rankIndicators.forEach(rankIndicator => {
      const area = rankIndicator.getRelatedElementArea(e);
      if (area > 0 && area < smallestArea) {
        smallestArea = area;
        smallestRankIndicator = rankIndicator;
      }
    });

    if (smallestRankIndicator) {
      if (this._hoveringRankIndicator !== smallestRankIndicator) {
        this._hoveringRankIndicator?.setHover(false);
        smallestRankIndicator.setHover(true);
        this._hoveringRankIndicator = smallestRankIndicator;
      }
    } else {
      this._hoveringRankIndicator?.setHover(false);
      this._hoveringRankIndicator = null;
    }
  }

  public layoutController(): LayoutController {
    return this._layoutController;
  }

  public layout(allowHighlightRelatedElements: boolean, animate: boolean = true) {
    this._layoutController.layoutElems(
      this._curHighlightedElems,
      this._rankIndicators,
      this.currentVisualInverseScale(),
      animate,
      allowHighlightRelatedElements,
    );
    if (this._curSelectedIdx >= 0) {
      this.select(this._curHighlightedElems[this._curSelectedIdx]);
    }
  }

  public select(elem: MatchingElement) {
    if (this._overlayMode === 'conversion' || this._overlayMode === 'area-clickmap') {
      this._curSelectedIdx = this._curHighlightedElems.indexOf(elem);
      if (this._overlayMode === 'area-clickmap') {
        this.layoutAreaClickMaps(this._areaClickMapDisplayOptions);
      } else if (this._overlayMode === 'conversion') {
        this.layoutConversionMaps(this._conversionAreaDisplayOptions);
      }
      return;
    }

    const selectIndicator = this._rankIndicators.get(elem);
    if (!selectIndicator) {
      return;
    }

    for (const [elem, rankIndicator] of this._rankIndicators) {
      const selected = rankIndicator === selectIndicator;
      rankIndicator.setSelected(selected);
      if (selected) {
        const rendererScale =
          1 /
          (this.currentZoomInverseScale() *
            (this.currentFrameInverseScale() / this.currentVisualInverseScale()));
        this._masker.highlight([elem]);
        this._layoutController.clampToViewport(rankIndicator, rendererScale);
        // TODO(jaime): Consider changing the selection API to pass an index and not the selector string so we don't have to linear scan here.
        this._curSelectedIdx = this._indexOfElem(elem);

        // TODO(jaime): Hack.
      } else if (rankIndicator.view().hasClass('offscreen')) {
        this._layoutController.clearOffscreenPositions(rankIndicator.view());
        rankIndicator.positionIndicator(rankIndicator.lastIndicatorRect());
      }
    }

    // TODO(jaime): See if we can combine this with the layout pass. For now just loop through AGAIN.
    const curPage = (this._curSelectedIdx / INDICATOR_PAGE_SIZE) | 0; // Integer flooring.

    this._curHighlightedElems.forEach((elem, i) => {
      if (((i / INDICATOR_PAGE_SIZE) | 0) != curPage) {
        selectIndicator.view().addClass('backgrounded');
      } else {
        selectIndicator.view().removeClass('backgrounded');
      }
    });
  }

  private _indexOfElem(elem: ElemBounds): number {
    for (let i = 0; i < this._curHighlightedElems.length; i++) {
      if (this._curHighlightedElems[i] == elem) {
        return i;
      }
    }
    return -1;
  }

  // Positions an element over the playback frame.
  // x and y are in the coordinate space of the playback frame.
  // You can optionally choose to have the width and height of the element you render *not* be scaled down to match the frame's coordinate space.
  public positionOverlayElem(
    elem: Q.OfOne,
    x: number,
    y: number,
    preserveWidthAndHeight?: boolean,
  ) {
    elem.setCss('--overlay-x', `${x}px`);
    elem.setCss('--overlay-y', `${y}px`);
    if (preserveWidthAndHeight) {
      // Optionally if we don't want to scale by the same factor as playback, and want a constant sized overlay.
      this.inverseScale(elem);
    }
  }

  public inverseScale(elem: Q.OfOne) {
    const inverseScale = this.currentVisualInverseScale();
    elem.setCss('--overlay-scale', inverseScale.toString());
  }

  public setPointerAndTouchIndicatorsVisible(value: boolean): void {
    this.#pointerAndTouchIndicatorsVisible = value;
    this.#updatePointerAndTouchVisibility();
  }

  #updatePointerAndTouchVisibility(): void {
    if (this.#pointerAndTouchIndicatorsVisible && this.#gotPointerEvent) {
      this.view().cursor.setCss('display', 'block');
    } else {
      this.view().cursor.setCss('display', 'none');
    }
    if (this.#pointerAndTouchIndicatorsVisible && this.#gotTouchEvent) {
      this.view().touches.setCss('display', 'block');
    } else {
      this.view().touches.setCss('display', 'none');
    }
  }

  public positionPointer(x: number, y: number): void {
    this.#gotPointerEvent = true;
    this.#updatePointerAndTouchVisibility();

    this.positionOverlayElem(this.view().cursor, x, y, true);
  }

  public touchDown(touchid: string | number, x: number, y: number) {
    this.#gotTouchEvent = true;
    this.#updatePointerAndTouchVisibility();

    let elem = this._curTouchElems[touchid];
    if (!elem) {
      elem = this._curTouchElems[touchid] = this.createTouchElement();
      this.view().touches.append(elem);
    }

    this.positionOverlayElem(elem, x, y, true);
  }

  public touchUp(touchid: string | number): void {
    const elem = this._curTouchElems[touchid];
    if (elem) {
      elem.addClass('up');
      setTimeout(function () {
        elem.remove();
      }, 250);
      delete this._curTouchElems[touchid];
    }
  }

  public positionTouch(touchid: string | number, x: number, y: number) {
    const elem = this._curTouchElems[touchid];
    if (elem) {
      this.positionOverlayElem(elem, x, y, true);
    }
  }

  public setTouchDebugText(touchid: string | number, text: string) {
    const elem = this._curTouchElems[touchid];
    if (elem) {
      elem.setAttr('fs-debug', text);
    }
  }

  public removeAllTouches() {
    if (this._curTouchElems) {
      for (const k in this._curTouchElems) {
        this._curTouchElems[k].remove();
      }
    }
    this._curTouchElems = {};
  }

  protected createTouchElement(): Q.OfOne {
    return Q.createOne('div').addClass('touch');
  }

  private getPlaybackScaleFactor({
    zoomInverseScale,
    frameInverseScale,
    visualInverseScale,
  }: {
    zoomInverseScale?: number;
    frameInverseScale?: number;
    visualInverseScale?: number;
  }) {
    return (
      (zoomInverseScale ?? this.currentZoomInverseScale()) *
      ((frameInverseScale ?? this.currentFrameInverseScale()) /
        (visualInverseScale ?? this.currentVisualInverseScale()))
    );
  }

  private getClickOverlayDetails = memoize((matchingElements: MatchingElement[]) => {
    const colors = new ColorMap(this._curHighlightedElems.map(x => x.stats().NumClicks));
    colors.clearAllGradientText();

    const clickOverlayDetails = matchingElements.map((elem, index) => ({
      rank: index + 1,
      clientRect: elem.r(),
      color: `map-key-color-${colors.getGradientIndex(elem.stats().NumClicks)}` as OverlayColors,
      elementStats: {
        ...elem.stats(),
        TooltipLines: [
          elem.stats().Stats[0]?.Val,
          elem.stats().Stats[1]?.Val,
          elem.stats().Footnote,
        ].filter(isNonNull),
      },
    }));

    return clickOverlayDetails;
  });

  private getConversionOverlayDetails = memoize(
    (matchingElements: MatchingElement[], elementConversionStats: ElementConversionStats) => {
      const colors = new ColorMap(
        matchingElements.map(
          elem =>
            elementConversionStats.elementStats[
              elemIdIdent(elem.stats().NamedElementId, elem.stats().PropertyValue)
            ]?.conversionRate,
        ),
      );
      colors.clearAllGradientText();

      return matchingElements.map((elem, index) => ({
        rank: index + 1,
        clientRect: elem.r(),
        color: `map-key-color-${colors.getGradientIndex(
          elementConversionStats.elementStats[
            elemIdIdent(elem.stats().NamedElementId, elem.stats().PropertyValue)
          ]?.conversionRate,
        )}` as OverlayColors,
        elementStats: {
          ...elem.stats(),
          TooltipLines: [],
        },
      }));
    },
  );

  private performCommonAreaHighlightsSetup({
    overlayMode,
    host,
    elems,
    playbackContainer,
    resizeOperation,
  }: {
    overlayMode: overlayMode;
    host: ActivityMapsHost;
    elems: MatchingElement[];
    playbackContainer: Q.OfOne;
    resizeOperation: (
      visualInverseScale: number,
      frameInverseScale: number,
      zoomInverseScale: number,
    ) => void;
  }) {
    if (this._masker) {
      this._masker.hide();
    }

    this._overlayMode = overlayMode;
    this._playbackContainer = playbackContainer;
    this._curHighlightedElems = elems.filter(isNonNull);
    this._activityMapsHost = host;

    // Remember all the things that might need to be resized.
    this._resizeOps.push(
      (visualInverseScale: number, frameInverseScale: number, zoomInverseScale: number) => {
        resizeOperation(visualInverseScale, frameInverseScale, zoomInverseScale);
      },
    );
  }
}
